import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Pill, AlertCircle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { authApi, useResetPassword } from '@/api/auth';

const MIN_LENGTH = 8;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const resetMut = useResetPassword();

  // On valide le lien AVANT d'afficher le formulaire : faire saisir deux fois un
  // mot de passe pour apprendre ensuite que le lien a expiré serait cruel.
  const check = useQuery({
    queryKey: ['auth', 'reset-token', token],
    queryFn: () => authApi.checkResetToken(token),
    enabled: token.length > 0,
    retry: false,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    try {
      await resetMut.mutateAsync({ token, password });
    } catch {
      // Erreur affichée via resetMut.error
    }
  }

  const shell = (children: React.ReactNode) => (
    <div className="w-full min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
            <Pill size={19} color="#fff" strokeWidth={2.25} />
          </div>
          <div>
            <div className="text-[17px] font-semibold text-slate-900">Officina</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">RH Pharmacie</div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-8">{children}</div>
      </div>
    </div>
  );

  // Lien absent, expiré ou déjà utilisé : une seule issue, en redemander un.
  if (!token || check.isError) {
    return shell(
      <>
        <div className="w-11 h-11 rounded-xl bg-danger-bg text-danger flex items-center justify-center mb-4">
          <ShieldAlert size={21} strokeWidth={2} />
        </div>
        <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">Lien inutilisable</h2>
        <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
          {token
            ? 'Ce lien de réinitialisation a expiré ou a déjà servi. Les liens ne valent qu’une heure, et une seule fois.'
            : 'Ce lien est incomplet. Ouvrez-le depuis le mail reçu, sans le modifier.'}
        </p>
        <Link
          to="/forgot-password"
          className="mt-6 w-full inline-flex items-center justify-center bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors"
        >
          Demander un nouveau lien
        </Link>
      </>
    );
  }

  if (check.isLoading) {
    return shell(<div className="text-[13px] text-slate-500 py-4">Vérification du lien…</div>);
  }

  if (resetMut.isSuccess) {
    return shell(
      <>
        <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
          <CheckCircle2 size={21} strokeWidth={2} />
        </div>
        <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">
          Mot de passe modifié
        </h2>
        <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
          Vous pouvez vous connecter avec votre nouveau mot de passe. Par sécurité, les sessions
          ouvertes sur vos autres appareils ont été fermées.
        </p>
        <button
          onClick={() => navigate('/login')}
          className="mt-6 w-full bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors"
        >
          Se connecter
        </button>
      </>
    );
  }

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;

  return shell(
    <>
      <h2 className="text-[24px] font-semibold tracking-tight text-slate-900">
        Nouveau mot de passe
      </h2>
      <p className="text-[13px] text-slate-500 mt-1">
        Pour <strong className="text-slate-700">{check.data?.email}</strong>
      </p>

      <form onSubmit={handleSubmit} className="space-y-4 mt-6">
        <div>
          <label className="label">Nouveau mot de passe</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            autoFocus
            required
          />
          <div className={`text-[11px] mt-1 ${tooShort ? 'text-danger' : 'text-slate-400'}`}>
            {MIN_LENGTH} caractères minimum
          </div>
        </div>

        <div>
          <label className="label">Confirmer</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setMismatch(false);
            }}
            className="input"
            required
          />
        </div>

        {mismatch && (
          <div className="bg-danger-bg border border-danger/30 rounded-lg p-2.5 flex items-start gap-2 text-[11.5px] text-danger">
            <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
            <span>Les deux mots de passe ne sont pas identiques.</span>
          </div>
        )}

        {resetMut.error && (
          <div className="bg-danger-bg border border-danger/30 rounded-lg p-2.5 flex items-start gap-2 text-[11.5px] text-danger">
            <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{(resetMut.error as Error).message || 'Modification impossible'}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={resetMut.isPending || password.length < MIN_LENGTH}
          className="w-full bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors disabled:opacity-50"
        >
          {resetMut.isPending ? 'Enregistrement…' : 'Modifier le mot de passe'}
        </button>
      </form>
    </>
  );
}
